import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertOctagon, CreditCard } from "lucide-react";
import { fmtDate } from "@/lib/datetime";

// Red banner listing pending invoices whose Autopay charge was declined.
export function AutopayDeclinesBanner({ refreshKey, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/admin/invoices/autopay-declines").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  if (!rows.length) return null;

  const retryNow = async (r) => {
    const amt = (Number(r.total || 0) * 1.03).toFixed(2);
    if (!window.confirm(`Retry charging invoice ${r.invoice_number} (${r.pharmacy_name}) — $${amt} incl. 3% card fee — to the saved card now?`)) return;
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/admin/invoices/${r.id}/charge-saved-card`);
      toast.success(`Charged $${Number(data.amount_usd).toFixed(2)} — ${r.invoice_number} marked Paid`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e), { duration: 10000 });
    } finally {
      setBusyId(null);
      load();
    }
  };

  return (
    <div className="rounded-xl border border-red-300 bg-red-50 text-red-900 p-4 space-y-3 mb-6" data-testid="autopay-declines-banner">
      <div className="flex items-center gap-2 font-semibold">
        <AlertOctagon className="h-4 w-4" />
        {rows.length} Autopay charge{rows.length === 1 ? "" : "s"} declined
      </div>
      <div className="divide-y divide-red-200">
        {rows.map((r) => (
          <div key={r.id} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid={`autopay-decline-row-${r.id}`}>
            <span className="font-mono font-semibold">{r.invoice_number}</span>
            <span>{r.pharmacy_name}</span>
            <span className="font-mono">${Number(r.total || 0).toFixed(2)}</span>
            <span className="text-red-700 flex-1 min-w-[200px]" data-testid={`autopay-decline-reason-${r.id}`}>
              {r.autopay_error_message || r.autopay_error_code || "Declined"}
              <span className="block text-[11px] text-red-600/80" data-testid={`autopay-decline-retry-${r.id}`}>
                {r.retry_pending
                  ? "Auto-retry: next business day at 9 AM ET"
                  : `Auto-retry also declined${r.autopay_retry_at ? ` on ${fmtDate(r.autopay_retry_at)}` : ""} — contact the pharmacy`}
              </span>
            </span>
            <Button
              size="sm"
              onClick={() => retryNow(r)}
              disabled={busyId === r.id}
              className="rounded-full h-8 px-3 bg-red-600 hover:bg-red-700 text-white"
              data-testid={`autopay-decline-retry-btn-${r.id}`}
            >
              <CreditCard className="h-3.5 w-3.5 mr-1" /> {busyId === r.id ? "Charging…" : "Retry now"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
